import { LinguiJSMessageDescriptor } from '@lib/types/common'
import { msgT } from '@lib/utils/i18n'
import React from 'react'
import ReactSelectDynamic from '@components/dynamic-components/react-select.dynamic'

import { theme as tailwindTheme } from '../../../tailwind.config'

type Props = {
  id: string
  label?: string
  helpText?: string
  errorText?: string
  placeholder?: string
  required?: boolean
  menuPlacement?: string
  value: {
    value: string
    label: string
  }
  options: {
    value: string
    label: string | LinguiJSMessageDescriptor
  }[]
  handleSelectChange: (
    id: string,
    obj: {
      value: string
      label: string
    }
  ) => void
  disabled?: boolean
  isInModal?: boolean
  isSearchable?: boolean
}

const customStyles = {
  container: (styles) => ({
    ...styles,
    fontSize: tailwindTheme.fontSize.base,
    lineHeight: tailwindTheme.lineHeight['tight'],
  }),
  control: (styles) => ({
    ...styles,
    borderColor: tailwindTheme.colors.grey[500],
    cursor: 'pointer',
  }),
  valueContainer: (styles) => ({ ...styles, height: '38px' }),
  input: (styles) => ({ ...styles, padding: '0 8px' }),
  singleValue: (styles) => ({ ...styles, padding: '8px', cursor: 'pointer' }),
  menu: (styles) => ({ ...styles, marginTop: '3px' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isDisabled
        ? null
        : isSelected
        ? tailwindTheme.colors.orange.default
        : isFocused
        ? tailwindTheme.colors.orange[100]
        : null,
      color: isDisabled
        ? tailwindTheme.colors.grey[300]
        : isSelected
        ? tailwindTheme.colors.white
        : data.color,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      ':active': {
        ...styles[':active'],
        backgroundColor:
          !isDisabled &&
          (isSelected ? data.color : tailwindTheme.colors.orange.default),
        color: !isDisabled && tailwindTheme.colors.white,
      },
    }
  },
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

/** i18n support added */
class SelectInput extends React.Component<Props> {
  public static defaultProps = {
    required: false,
    isSearchable: true,
  }

  onChange = (selectedOption: { value: string; label: string }) => {
    this.props.handleSelectChange(this.props.id, {
      value: selectedOption.value,
      label: selectedOption.label,
    })
  }

  render() {
    const {
      label,
      id,
      placeholder,
      value,
      menuPlacement,
      options,
      required,
      helpText,
      errorText,
      disabled,
      isInModal,
      isSearchable,
    } = this.props

    return (
      <>
        <div className="text-grey text-md font-600">{label}</div>
        <div className="mt-2">
          <ReactSelectDynamic
            styles={customStyles}
            id={id}
            instanceId={id}
            placeholder={placeholder}
            value={value}
            onChange={this.onChange}
            options={options.map(({ label, value }) => ({
              value,
              label: msgT(label),
            }))}
            isSearchable={isSearchable}
            required={required}
            menuPlacement={menuPlacement as any}
            menuPortalTarget={isInModal ? document.body : null}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: tailwindTheme.colors.orange.default,
              },
            })}
            isDisabled={disabled}
          />
        </div>
        {helpText && (
          <div className="py-2 text-sm text-grey font-400">{helpText}</div>
        )}
        {errorText && (
          <div className="py-2 text-sm text-red-200 font-400">{errorText}</div>
        )}
      </>
    )
  }
}

export default React.memo(SelectInput)
